import React from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

const sizeMap = {
    sm: 'text-sm py-2 px-3',
    md: 'text-base py-3 px-4',
    lg: 'text-lg py-4 px-5',
};

const variantMap = {
    simple: 'text-gray-900',
    muted: 'text-gray-600',
    subtle: 'text-gray-500',
};

export default function List({
    items = [],
    renderItem,
    size = 'md',
    variant = 'simple',
    divider = true,
    striped = false,
    selectable = false,
    selectedItems = [],
    onSelect,
    onItemClick,
    emptyMessage = 'No items to display',
    className = '',
    style,
}) {
    const handleItemClick = (item, index) => {
        if (selectable) {
            const newSelected = selectedItems.includes(index)
                ? selectedItems.filter((i) => i !== index)
                : [...selectedItems, index];
            onSelect?.(newSelected);
        }
        onItemClick?.(item, index);
    };

    if (!items || items.length === 0) {
        return (
            <div className={cx('p-4 text-center text-gray-500', className)} style={style}>
                {emptyMessage}
            </div>
        );
    }

    return (
        <ul className={cx('rounded-lg border border-gray-200 overflow-hidden', className)} style={style}>
            {items.map((item, index) => (
                <li
                    key={index}
                    className={cx(
                        'flex items-center cursor-pointer transition-colors',
                        sizeMap[size] || sizeMap.md,
                        divider && index !== items.length - 1 && 'border-b border-gray-200',
                        striped && index % 2 !== 0 && 'bg-gray-50',
                        selectedItems.includes(index) && 'bg-blue-50',
                        'hover:bg-gray-100'
                    )}
                    onClick={() => handleItemClick(item, index)}
                >
                    {/* Checkbox */}
                    {selectable && (
                        <input
                            type="checkbox"
                            checked={selectedItems.includes(index)}
                            onChange={() => {}}
                            className="ml-3 mr-3 h-4 w-4 cursor-pointer accent-blue-600"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select item ${index + 1}`}
                        />
                    )}

                    {/* Content */}
                    <div className="flex-1 truncate">
                        {typeof renderItem === 'function' ? (
                            renderItem(item, index)
                        ) : typeof item === 'string' ? (
                            <span className={variantMap[variant]}>{item}</span>
                        ) : item.label ? (
                            <div>
                                <div className="font-medium text-gray-900">{item.label}</div>
                                {item.description && <div className="text-sm text-gray-500 truncate">{item.description}</div>}
                            </div>
                        ) : item.message || item.name || item.title ? (
                            <div>
                                <div className="font-medium text-gray-900">{item.message || item.name || item.title}</div>
                                {(item.description || item.timestamp || item.status) && (
                                    <div className="text-sm text-gray-500 truncate">
                                        {item.description || [item.status, item.timestamp].filter(Boolean).join(' · ')}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className={variantMap[variant]}>{JSON.stringify(item)}</span>
                        )}
                    </div>

                    {/* Icon or Action */}
                    {item.icon && <span className="ml-3 mr-3 flex-shrink-0">{item.icon}</span>}
                </li>
            ))}
        </ul>
    );
}